// ComplianceCheckerDashboard.js - Compliance Checker (Second-Leg) Dashboard
// Focus: KYC confirmation, CDS signing, screening review, document view, periodic reviews, audit trail

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, CheckCircle, Clock, Eye, FileText, Search, RefreshCw,
  UserCheck, ArrowUpRight, ArrowDownRight, X, Calendar, CheckCircle2,
  History, CreditCard, MessageSquare, Pen, RotateCcw, Send, ArrowLeft,
  AlertTriangle, Download
} from 'lucide-react';
import { getStatusStyle, getIconStyle, getTrendStyle } from '../../config/statusColors';
import { useAuth } from '../../context/AuthContext';

// ============================================
// HELPER COMPONENTS
// ============================================
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-none shadow-xl ${sizeClasses[size]} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-6 border-b border-zinc-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-zinc-900">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-none"><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, trend, subtitle, onClick }) => (
  <div className={`bg-white rounded-none shadow-sm p-5 border border-zinc-100 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={onClick}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm text-zinc-500 font-medium">{title}</p>
        <p className="text-2xl font-bold text-zinc-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
        {trend !== undefined && (
          <div className="flex items-center gap-1 mt-2 text-sm" style={getTrendStyle(trend >= 0)}>
            {trend >= 0 ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
            <span>{Math.abs(trend)}% vs last month</span>
          </div>
        )}
      </div>
      <div className="p-3 rounded-none" style={getIconStyle(color)}><Icon className="w-5 h-5" /></div>
    </div>
  </div>
);

// ============================================
// URL PATH TO TAB MAPPING
// ============================================
const getTabFromPath = (pathname) => {
  if (pathname.includes('/kyc')) return 'kyc_confirmation';
  if (pathname.includes('/cds')) return 'cds_signing';
  if (pathname.includes('/screening')) return 'screening';
  if (pathname.includes('/documents')) return 'documents';
  if (pathname.includes('/reviews')) return 'periodic_reviews';
  if (pathname.includes('/audit')) return 'audit';
  return 'kyc_confirmation';
};

// ============================================
// MOCK DATA
// ============================================
const mockKycCases = [
  { id: 'KYC-2024-0091', investorName: 'Meridian Capital Partners', makerId: 'usr-maker-001', makerName: 'Jane Wanjiku', approvalDate: '2024-11-28', riskLevel: 'low', status: 'pending_approval', notes: 'Standard individual KYC, all documents verified by maker.' },
  { id: 'KYC-2024-0088', investorName: 'Sahara Growth Fund', makerId: 'usr-maker-002', makerName: 'David Omondi', approvalDate: '2024-11-27', riskLevel: 'high', status: 'pending_approval', notes: 'PEP match flagged on director. Enhanced due diligence completed.' },
  { id: 'KYC-2024-0085', investorName: 'Atlas Infrastructure Ltd', makerId: 'usr-maker-001', makerName: 'Jane Wanjiku', approvalDate: '2024-11-26', riskLevel: 'moderate', status: 'pending_approval', notes: 'Corporate entity, beneficial ownership verified.' },
  { id: 'KYC-2024-0082', investorName: 'Nordic Pension Alliance', makerId: 'usr-maker-003', makerName: 'Samuel Njoroge', approvalDate: '2024-11-25', riskLevel: 'low', status: 'pending_approval', notes: 'Institutional investor, regulated entity.' },
  { id: 'KYC-2024-0079', investorName: 'Baobab Wealth Management', makerId: 'usr-maker-002', makerName: 'David Omondi', approvalDate: '2024-11-24', riskLevel: 'moderate', status: 'pending_approval', notes: 'Trust structure, UBO declaration provided.' },
  { id: 'KYC-2024-0076', investorName: 'East Africa Ventures', makerId: 'usr-maker-001', makerName: 'Jane Wanjiku', approvalDate: '2024-11-23', riskLevel: 'high', status: 'pending_approval', notes: 'Multi-jurisdictional entity. Source of funds requires review.' },
];

const mockCdsCards = [
  { id: 'CDS-4410', investorName: 'Meridian Capital Partners', bank: 'Stanbic Bank Kenya', status: 'cds_card_pending', signatures: 0, firstSigner: 'checker', createdDate: '2024-11-28' },
  { id: 'CDS-4407', investorName: 'Sahara Growth Fund', bank: 'Stanbic Bank Kenya', status: 'cds_card_pending', signatures: 0, firstSigner: 'checker', createdDate: '2024-11-27' },
  { id: 'CDS-4403', investorName: 'Atlas Infrastructure Ltd', bank: 'KCB Group', status: 'cds_card_pending', signatures: 0, firstSigner: 'maker', createdDate: '2024-11-26' },
  { id: 'CDS-4399', investorName: 'Nordic Pension Alliance', bank: 'Stanbic Bank Kenya', status: 'cds_card_checker_signed', signatures: 1, firstSigner: 'checker', createdDate: '2024-11-25' },
  { id: 'CDS-4395', investorName: 'Baobab Wealth Management', bank: 'Equity Bank', status: 'cds_card_complete', signatures: 2, firstSigner: 'checker', createdDate: '2024-11-23' },
];

const mockScreeningAlerts = [
  { id: 'SCR-7701', investorName: 'Sahara Growth Fund', type: 'PEP', matchScore: 92, status: 'pending', details: 'Director name matches PEP database entry for senior government official.' },
  { id: 'SCR-7698', investorName: 'East Africa Ventures', type: 'Sanctions', matchScore: 78, status: 'investigating', details: 'Partial name match against OFAC SDN list.' },
  { id: 'SCR-7695', investorName: 'Atlas Infrastructure Ltd', type: 'Adverse Media', matchScore: 65, status: 'cleared', details: 'Media article references a different entity with similar name.' },
  { id: 'SCR-7692', investorName: 'Baobab Wealth Management', type: 'PEP', matchScore: 45, status: 'cleared', details: 'Low-confidence match, different date of birth confirmed.' },
  { id: 'SCR-7689', investorName: 'Nordic Pension Alliance', type: 'Sanctions', matchScore: 88, status: 'escalated', details: 'Country of incorporation on restricted list. Awaiting legal review.' },
];

const mockDocuments = [
  { id: 'DOC-3301', name: 'Meridian Capital - Passport Copy', type: 'Identity', uploadedBy: 'Jane Wanjiku', date: '2024-11-28', status: 'verified', caseRef: 'KYC-2024-0091' },
  { id: 'DOC-3298', name: 'Sahara Growth Fund - Certificate of Incorporation', type: 'Corporate', uploadedBy: 'David Omondi', date: '2024-11-27', status: 'verified', caseRef: 'KYC-2024-0088' },
  { id: 'DOC-3295', name: 'Atlas Infrastructure - UBO Declaration', type: 'Ownership', uploadedBy: 'Jane Wanjiku', date: '2024-11-26', status: 'pending', caseRef: 'KYC-2024-0085' },
  { id: 'DOC-3292', name: 'Nordic Pension - Regulatory License', type: 'Regulatory', uploadedBy: 'Samuel Njoroge', date: '2024-11-25', status: 'verified', caseRef: 'KYC-2024-0082' },
  { id: 'DOC-3289', name: 'Baobab Wealth - Source of Funds Letter', type: 'Financial', uploadedBy: 'David Omondi', date: '2024-11-24', status: 'pending', caseRef: 'KYC-2024-0079' },
];

const mockPeriodicReviews = [
  { id: 'PRV-1101', investorName: 'Meridian Capital Partners', lastReviewDate: '2023-11-15', nextReviewDate: '2024-11-15', status: 'overdue', riskLevel: 'low' },
  { id: 'PRV-1098', investorName: 'Sahara Growth Fund', lastReviewDate: '2024-05-20', nextReviewDate: '2025-05-20', status: 'current', riskLevel: 'high' },
  { id: 'PRV-1095', investorName: 'Atlas Infrastructure Ltd', lastReviewDate: '2024-02-10', nextReviewDate: '2025-02-10', status: 'current', riskLevel: 'moderate' },
  { id: 'PRV-1092', investorName: 'Nordic Pension Alliance', lastReviewDate: '2023-08-01', nextReviewDate: '2024-08-01', status: 'overdue', riskLevel: 'low' },
  { id: 'PRV-1089', investorName: 'Baobab Wealth Management', lastReviewDate: '2024-09-12', nextReviewDate: '2025-09-12', status: 'current', riskLevel: 'moderate' },
  { id: 'PRV-1086', investorName: 'East Africa Ventures', lastReviewDate: '2024-01-05', nextReviewDate: '2025-01-05', status: 'expiring', riskLevel: 'high' },
];

const mockAuditTrail = [
  { id: 'AUD-9901', timestamp: '2024-11-28 14:32:10', action: 'KYC Confirmed', user: 'Peter Kamau', details: 'Confirmed KYC for Meridian Capital Partners. Risk level: Low.', caseRef: 'KYC-2024-0091' },
  { id: 'AUD-9898', timestamp: '2024-11-28 11:15:44', action: 'CDS Card Signed', user: 'Peter Kamau', details: 'First signature applied to CDS card CDS-4410.', caseRef: 'CDS-4410' },
  { id: 'AUD-9895', timestamp: '2024-11-27 16:48:22', action: 'KYC Returned', user: 'Peter Kamau', details: 'Returned KYC case to maker. Missing source of funds.', caseRef: 'KYC-2024-0070' },
  { id: 'AUD-9892', timestamp: '2024-11-27 10:05:33', action: 'Screening Escalated', user: 'Peter Kamau', details: 'Escalated PEP match for Sahara Growth Fund.', caseRef: 'SCR-7701' },
  { id: 'AUD-9889', timestamp: '2024-11-26 15:22:18', action: 'Review Initiated', user: 'Peter Kamau', details: 'Initiated periodic review for East Africa Ventures.', caseRef: 'PRV-1086' },
  { id: 'AUD-9886', timestamp: '2024-11-26 09:41:55', action: 'Document Commented', user: 'Peter Kamau', details: 'Commented on UBO Declaration: "Beneficial owner chain verified."', caseRef: 'DOC-3295' },
  { id: 'AUD-9883', timestamp: '2024-11-25 14:10:07', action: 'KYC Confirmed', user: 'Peter Kamau', details: 'Confirmed KYC for Nordic Pension Alliance.', caseRef: 'KYC-2024-0082' },
  { id: 'AUD-9880', timestamp: '2024-11-25 11:33:29', action: 'CDS Card Signed', user: 'Peter Kamau', details: 'First signature applied to CDS card CDS-4399.', caseRef: 'CDS-4399' },
];

// ============================================
// MAIN COMPLIANCE CHECKER DASHBOARD
// ============================================
const ComplianceCheckerDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentUserId = user?.id || '';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  useEffect(() => { setActiveTab(getTabFromPath(location.pathname)); }, [location.pathname]);

  // Data state
  const [kycCases, setKycCases] = useState(mockKycCases);
  const [cdsCards, setCdsCards] = useState(mockCdsCards);
  const [screeningAlerts] = useState(mockScreeningAlerts);
  const [documents] = useState(mockDocuments);
  const [periodicReviews, setPeriodicReviews] = useState(mockPeriodicReviews);
  const [auditTrail, setAuditTrail] = useState(mockAuditTrail);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [returnReason, setReturnReason] = useState('');

  useEffect(() => { const t = setTimeout(() => setLoading(false), 600); return () => clearTimeout(t); }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tabToPath = {
      kyc_confirmation: '/bank/compliance-checker',
      cds_signing: '/bank/compliance-checker/cds',
      screening: '/bank/compliance-checker/screening',
      documents: '/bank/compliance-checker/documents',
      periodic_reviews: '/bank/compliance-checker/reviews',
      audit: '/bank/compliance-checker/audit',
    };
    if (tabToPath[tabId]) navigate(tabToPath[tabId]);
  };

  const handleRefresh = () => { setRefreshing(true); setTimeout(() => setRefreshing(false), 800); };

  const addAudit = (action, details, caseRef) => {
    setAuditTrail(prev => [{ id: `AUD-${Date.now()}`, timestamp: new Date().toISOString().replace('T', ' ').slice(0, 19), action, user: user?.name || 'Compliance Checker', details, caseRef }, ...prev]);
  };

  // KYC Actions
  const handleConfirmKyc = (caseId) => {
    setKycCases(prev => prev.filter(c => c.id !== caseId));
    addAudit('KYC Confirmed', `Confirmed KYC case ${caseId}.`, caseId);
    setShowDetailModal(false);
  };

  const handleReturnKyc = (caseId) => {
    if (!returnReason.trim()) return;
    setKycCases(prev => prev.filter(c => c.id !== caseId));
    addAudit('KYC Returned', `Returned ${caseId}. Reason: ${returnReason}`, caseId);
    setReturnReason('');
    setShowDetailModal(false);
  };

  // CDS Actions
  const handleSignCds = (cardId) => {
    setCdsCards(prev => prev.map(c => c.id === cardId ? { ...c, signatures: 1, status: 'cds_card_checker_signed' } : c));
    addAudit('CDS Card Signed', `First signature applied to ${cardId}.`, cardId);
  };

  // Screening Actions
  const handleEscalateAlert = (alertId) => {
    addAudit('Screening Escalated', `Escalated alert ${alertId} to Head of Compliance.`, alertId);
  };

  // Review Actions
  const handleInitiateReview = (reviewId) => {
    setPeriodicReviews(prev => prev.map(r => r.id === reviewId ? { ...r, status: 'review' } : r));
    addAudit('Review Initiated', `Initiated periodic review ${reviewId}.`, reviewId);
  };

  const handleCompleteReview = (reviewId) => {
    const nextDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setPeriodicReviews(prev => prev.map(r => r.id === reviewId ? { ...r, status: 'completed', lastReviewDate: new Date().toISOString().split('T')[0], nextReviewDate: nextDate } : r));
    addAudit('Review Completed', `Marked review ${reviewId} as complete.`, reviewId);
  };

  // CRITICAL: Filter out cases where makerId === currentUserId (four-eyes principle)
  const filteredKycCases = kycCases.filter(c => c.makerId !== currentUserId).filter(c => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || c.investorName.toLowerCase().includes(s) || c.id.toLowerCase().includes(s);
    const matchFilter = statusFilter === 'all' || c.riskLevel === statusFilter;
    return matchSearch && matchFilter;
  });

  const filteredCdsCards = cdsCards.filter(c => {
    const s = searchTerm.toLowerCase();
    return !s || c.investorName.toLowerCase().includes(s) || c.id.toLowerCase().includes(s);
  });

  const filteredScreening = screeningAlerts.filter(a => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || a.investorName.toLowerCase().includes(s) || a.id.toLowerCase().includes(s);
    return matchSearch && (statusFilter === 'all' || a.status === statusFilter);
  });

  const filteredDocuments = documents.filter(d => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || d.name.toLowerCase().includes(s) || d.id.toLowerCase().includes(s);
    return matchSearch && (statusFilter === 'all' || d.status === statusFilter);
  });

  const filteredReviews = periodicReviews.filter(r => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || r.investorName.toLowerCase().includes(s) || r.id.toLowerCase().includes(s);
    return matchSearch && (statusFilter === 'all' || r.status === statusFilter);
  });

  const filteredAudit = auditTrail.filter(a => {
    const s = searchTerm.toLowerCase();
    return !s || a.details.toLowerCase().includes(s) || a.caseRef.toLowerCase().includes(s) || a.action.toLowerCase().includes(s);
  });

  // Metrics
  const pendingConfirmations = kycCases.filter(c => c.makerId !== currentUserId).length;
  const today = new Date().toISOString().slice(0, 10);
  const confirmedToday = auditTrail.filter(a => a.action === 'KYC Confirmed' && a.timestamp.startsWith(today)).length;
  const returnedToday = auditTrail.filter(a => a.action === 'KYC Returned' && a.timestamp.startsWith(today)).length;
  const reviewsDue = periodicReviews.filter(r => r.status === 'overdue' || r.status === 'expiring').length;

  const closeModal = () => { setShowDetailModal(false); setSelectedItem(null); setReturnReason(''); setCommentText(''); };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zinc-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-zinc-600 mt-4">Loading compliance checker data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-600 to-zinc-800 rounded-none p-6 text-white mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-6 h-6" />
              <span className="text-zinc-200 text-sm font-medium">COMPLIANCE CHECKER</span>
            </div>
            <h1 className="text-2xl font-bold">
              {user?.tenantName || JSON.parse(localStorage.getItem('forwardsflow_user') || '{}').tenantName || 'Bank'} &bull; Compliance Checker
            </h1>
            <p className="text-zinc-100 mt-1">{pendingConfirmations} pending confirmations &bull; {reviewsDue} reviews due</p>
          </div>
          <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-none text-sm transition-colors">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Pending Confirmations" value={pendingConfirmations} icon={Clock} color="amber" subtitle="KYC cases awaiting second-leg approval" />
        <StatCard title="Confirmed Today" value={confirmedToday} icon={CheckCircle} color="green" subtitle="Cases confirmed in current session" />
        <StatCard title="Returned Today" value={returnedToday} icon={ArrowLeft} color="red" subtitle="Cases returned to maker" />
        <StatCard title="Reviews Due" value={reviewsDue} icon={Calendar} color="blue" subtitle="Periodic reviews overdue or expiring" onClick={() => handleTabChange('periodic_reviews')} />
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 bg-white rounded-none p-1 shadow-sm overflow-x-auto">
        {[
          { id: 'kyc_confirmation', label: 'KYC Confirmation', icon: UserCheck, count: pendingConfirmations },
          { id: 'cds_signing', label: 'CDS Signing', icon: CreditCard, count: cdsCards.filter(c => c.signatures === 0).length },
          { id: 'screening', label: 'Screening', icon: Search, count: screeningAlerts.filter(a => a.status === 'pending' || a.status === 'investigating').length },
          { id: 'documents', label: 'Documents', icon: FileText, count: documents.length },
          { id: 'periodic_reviews', label: 'Periodic Reviews', icon: RotateCcw, count: reviewsDue },
          { id: 'audit', label: 'Audit Trail', icon: History },
        ].map(tab => (
          <button key={tab.id} onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-colors whitespace-nowrap ${activeTab === tab.id ? 'bg-zinc-600 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-none text-xs ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-700'}`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ============================================ */}
      {/* KYC CONFIRMATION TAB */}
      {/* ============================================ */}
      {activeTab === 'kyc_confirmation' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search by Case ID or Investor Name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500">
              <option value="all">All Risk Levels</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <h3 className="text-lg font-semibold text-zinc-900">KYC Cases Awaiting Confirmation</h3>
              <p className="text-sm text-zinc-500 mt-1">Cases approved by Maker, awaiting your second-leg confirm or return. Your own maker cases are excluded.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Case ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Investor Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Maker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Approval Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Risk Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredKycCases.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">No KYC cases pending confirmation.</td></tr>
                  ) : filteredKycCases.map(c => (
                    <tr key={c.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3"><p className="font-mono text-sm text-zinc-600">{c.id}</p></td>
                      <td className="px-4 py-3"><p className="text-sm font-medium text-zinc-900">{c.investorName}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{c.makerName}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{c.approvalDate}</p></td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(c.riskLevel)}>{c.riskLevel}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setSelectedItem(c); setShowDetailModal(true); }} className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="View"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => handleConfirmKyc(c.id)} className="flex items-center gap-1 px-3 py-1 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-xs font-medium"><CheckCircle2 className="w-3 h-3" />Confirm</button>
                          <button onClick={() => { setSelectedItem(c); setShowDetailModal(true); }} className="flex items-center gap-1 px-3 py-1 border border-zinc-300 text-zinc-600 rounded-none hover:bg-zinc-50 text-xs font-medium"><ArrowLeft className="w-3 h-3" />Return</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CDS SIGNING TAB */}
      {/* ============================================ */}
      {activeTab === 'cds_signing' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search CDS cards..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCdsCards.length === 0 ? (
              <div className="col-span-full text-center py-8 text-zinc-400">No CDS cards found.</div>
            ) : filteredCdsCards.map(card => (
              <div key={card.id} className="bg-white rounded-none shadow-sm border border-zinc-100 p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm text-zinc-500">{card.id}</p>
                    <p className="text-base font-semibold text-zinc-900 mt-1">{card.investorName}</p>
                  </div>
                  <span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(card.status)}>{card.status.replace(/_/g, ' ')}</span>
                </div>
                <div className="space-y-2 text-sm text-zinc-600 mb-4">
                  <div className="flex justify-between"><span>Bank</span><span className="font-medium text-zinc-900">{card.bank}</span></div>
                  <div className="flex justify-between"><span>Created</span><span className="font-medium text-zinc-900">{card.createdDate}</span></div>
                  <div className="flex justify-between"><span>Signatures</span><span className="font-medium text-zinc-900">{card.signatures} / 2</span></div>
                  <div className="flex justify-between"><span>First Signer</span><span className="font-medium text-zinc-900 capitalize">{card.firstSigner}</span></div>
                </div>
                <div className="mb-4">
                  <div className="flex gap-2">
                    <div className={`flex-1 h-2 rounded-none ${card.signatures >= 1 ? 'bg-zinc-600' : 'bg-zinc-200'}`} />
                    <div className={`flex-1 h-2 rounded-none ${card.signatures >= 2 ? 'bg-zinc-600' : 'bg-zinc-200'}`} />
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">{card.signatures === 0 ? 'Awaiting first signature' : card.signatures === 1 ? 'Awaiting second signature' : 'Fully signed'}</p>
                </div>
                {card.signatures === 0 && card.firstSigner === 'checker' && (
                  <button onClick={() => handleSignCds(card.id)} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-sm font-medium">
                    <Pen className="w-4 h-4" />Sign CDS Card
                  </button>
                )}
                {card.signatures === 0 && card.firstSigner !== 'checker' && (
                  <p className="text-xs text-zinc-400 text-center py-2">Waiting for {card.firstSigner} to sign first</p>
                )}
                {card.signatures === 1 && <p className="text-xs text-zinc-400 text-center py-2">Awaiting maker counter-signature</p>}
                {card.signatures === 2 && (
                  <div className="flex items-center justify-center gap-1 text-xs py-2" style={getStatusStyle('completed')}><CheckCircle className="w-3 h-3" /><span>Fully Signed</span></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* SCREENING TAB (Read-Only) */}
      {/* ============================================ */}
      {activeTab === 'screening' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search alerts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500">
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="investigating">Investigating</option>
              <option value="escalated">Escalated</option>
              <option value="cleared">Cleared</option>
            </select>
          </div>
          <div className="bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <h3 className="text-lg font-semibold text-zinc-900">Screening Results</h3>
              <p className="text-sm text-zinc-500 mt-1">AML/CFT screening alerts. Read-only view with escalation and comment capabilities.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Alert ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Investor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Match Score</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredScreening.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">No screening alerts found.</td></tr>
                  ) : filteredScreening.map(alert => (
                    <tr key={alert.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3"><p className="font-mono text-sm text-zinc-600">{alert.id}</p></td>
                      <td className="px-4 py-3"><p className="text-sm font-medium text-zinc-900">{alert.investorName}</p></td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium bg-zinc-100 text-zinc-700">{alert.type}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-zinc-200 rounded-none overflow-hidden">
                            <div className={`h-full rounded-none ${alert.matchScore >= 80 ? 'bg-red-400' : alert.matchScore >= 60 ? 'bg-amber-400' : 'bg-zinc-400'}`} style={{ width: `${alert.matchScore}%` }} />
                          </div>
                          <span className="text-sm font-mono text-zinc-600">{alert.matchScore}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(alert.status)}>{alert.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setSelectedItem(alert); setShowDetailModal(true); }} className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="View"><Eye className="w-4 h-4" /></button>
                          {alert.status !== 'cleared' && alert.status !== 'escalated' && (
                            <button onClick={() => handleEscalateAlert(alert.id)} className="flex items-center gap-1 px-3 py-1 border border-zinc-300 text-zinc-600 rounded-none hover:bg-zinc-50 text-xs font-medium"><AlertTriangle className="w-3 h-3" />Escalate</button>
                          )}
                          <button onClick={() => { setSelectedItem(alert); setCommentText(''); setShowDetailModal(true); }} className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="Comment"><MessageSquare className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DOCUMENTS TAB (Read-Only) */}
      {/* ============================================ */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search documents..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500">
              <option value="all">All Statuses</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <h3 className="text-lg font-semibold text-zinc-900">Documents</h3>
              <p className="text-sm text-zinc-500 mt-1">View and comment on uploaded KYC documents. Upload is restricted to Maker role.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Uploaded By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredDocuments.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">No documents found.</td></tr>
                  ) : filteredDocuments.map(doc => (
                    <tr key={doc.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-zinc-400" />
                          <div>
                            <p className="text-sm font-medium text-zinc-900">{doc.name}</p>
                            <p className="text-xs font-mono text-zinc-400">{doc.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium bg-zinc-100 text-zinc-700">{doc.type}</span></td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{doc.uploadedBy}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{doc.date}</p></td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(doc.status)}>{doc.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setSelectedItem(doc); setShowDetailModal(true); }} className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="View"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => { setSelectedItem(doc); setCommentText(''); setShowDetailModal(true); }} className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="Comment"><MessageSquare className="w-4 h-4" /></button>
                          <button className="p-1.5 hover:bg-zinc-100 rounded-none text-zinc-500 hover:text-zinc-700" title="Download"><Download className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* PERIODIC REVIEWS TAB */}
      {/* ============================================ */}
      {activeTab === 'periodic_reviews' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search reviews..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500">
              <option value="all">All Statuses</option>
              <option value="current">Current</option>
              <option value="overdue">Overdue</option>
              <option value="expiring">Expiring</option>
              <option value="review">In Review</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <h3 className="text-lg font-semibold text-zinc-900">Scheduled Re-KYC Reviews</h3>
              <p className="text-sm text-zinc-500 mt-1">Periodic re-verification schedule for all onboarded investors.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Investor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Last Review Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Next Review Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Risk Level</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredReviews.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">No periodic reviews found.</td></tr>
                  ) : filteredReviews.map(r => (
                    <tr key={r.id} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3">
                        <div><p className="text-sm font-medium text-zinc-900">{r.investorName}</p><p className="text-xs font-mono text-zinc-400">{r.id}</p></div>
                      </td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{r.lastReviewDate}</p></td>
                      <td className="px-4 py-3"><p className="text-sm text-zinc-600">{r.nextReviewDate}</p></td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(r.status)}>{r.status}</span></td>
                      <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(r.riskLevel)}>{r.riskLevel}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {(r.status === 'overdue' || r.status === 'expiring' || r.status === 'current') && (
                            <button onClick={() => handleInitiateReview(r.id)} className="flex items-center gap-1 px-3 py-1 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-xs font-medium"><RotateCcw className="w-3 h-3" />Initiate</button>
                          )}
                          {r.status === 'review' && (
                            <button onClick={() => handleCompleteReview(r.id)} className="flex items-center gap-1 px-3 py-1 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-xs font-medium"><CheckCircle2 className="w-3 h-3" />Complete</button>
                          )}
                          {r.status === 'completed' && <span className="text-xs text-zinc-400">Completed</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* AUDIT TRAIL TAB */}
      {/* ============================================ */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input type="text" placeholder="Search audit trail..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-zinc-200 rounded-none focus:outline-none focus:ring-2 focus:ring-zinc-500" />
            </div>
          </div>
          <div className="bg-white rounded-none shadow-sm border border-zinc-100 overflow-hidden">
            <div className="p-6 border-b border-zinc-100">
              <h3 className="text-lg font-semibold text-zinc-900">Audit Trail</h3>
              <p className="text-sm text-zinc-500 mt-1">Complete log of all compliance checker actions and events.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Details</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-500 uppercase">Case Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredAudit.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400">No audit entries found.</td></tr>
                  ) : filteredAudit.map(entry => {
                    const actionMap = { 'KYC Confirmed': 'approved', 'KYC Returned': 'rejected', 'CDS Card Signed': 'submitted', 'Screening Escalated': 'escalated', 'Review Initiated': 'pending', 'Review Completed': 'completed', 'Document Commented': 'draft', 'Screening Comment': 'draft' };
                    return (
                      <tr key={entry.id} className="hover:bg-zinc-50 transition-colors">
                        <td className="px-4 py-3"><p className="text-sm font-mono text-zinc-600 whitespace-nowrap">{entry.timestamp}</p></td>
                        <td className="px-4 py-3"><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(actionMap[entry.action] || 'pending')}>{entry.action}</span></td>
                        <td className="px-4 py-3"><p className="text-sm text-zinc-600">{entry.user}</p></td>
                        <td className="px-4 py-3"><p className="text-sm text-zinc-600 max-w-md truncate" title={entry.details}>{entry.details}</p></td>
                        <td className="px-4 py-3"><p className="font-mono text-sm text-zinc-500">{entry.caseRef}</p></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DETAIL / RETURN MODAL */}
      {/* ============================================ */}
      <Modal isOpen={showDetailModal} onClose={closeModal} title={selectedItem ? (selectedItem.id || selectedItem.name || 'Details') : 'Details'} size="md">
        {selectedItem && (
          <div className="space-y-4">
            {/* KYC Case Detail */}
            {selectedItem.makerId && (<>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-zinc-500">Investor</p><p className="font-medium text-zinc-900">{selectedItem.investorName}</p></div>
                <div><p className="text-zinc-500">Maker</p><p className="font-medium text-zinc-900">{selectedItem.makerName}</p></div>
                <div><p className="text-zinc-500">Approval Date</p><p className="font-medium text-zinc-900">{selectedItem.approvalDate}</p></div>
                <div><p className="text-zinc-500">Risk Level</p><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(selectedItem.riskLevel)}>{selectedItem.riskLevel}</span></div>
              </div>
              <div>
                <p className="text-zinc-500 text-sm mb-1">Maker Notes</p>
                <div className="p-3 bg-zinc-50 rounded-none text-sm text-zinc-700 border border-zinc-100">{selectedItem.notes}</div>
              </div>
              <hr className="border-zinc-100" />
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Return Reason (required to return)</label>
                <textarea value={returnReason} onChange={(e) => setReturnReason(e.target.value)} placeholder="Provide a reason for returning this case to the maker..."
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500" rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => handleReturnKyc(selectedItem.id)} disabled={!returnReason.trim()}
                  className="flex items-center gap-2 px-4 py-2 border border-zinc-300 text-zinc-600 rounded-none hover:bg-zinc-50 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <ArrowLeft className="w-4 h-4" />Return to Maker
                </button>
                <button onClick={() => handleConfirmKyc(selectedItem.id)} className="flex items-center gap-2 px-4 py-2 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />Confirm KYC
                </button>
              </div>
            </>)}

            {/* Screening Alert Detail */}
            {selectedItem.matchScore !== undefined && (<>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-zinc-500">Investor</p><p className="font-medium text-zinc-900">{selectedItem.investorName}</p></div>
                <div><p className="text-zinc-500">Type</p><span className="px-2 py-1 rounded-none text-xs font-medium bg-zinc-100 text-zinc-700">{selectedItem.type}</span></div>
                <div><p className="text-zinc-500">Match Score</p><p className="font-medium font-mono text-zinc-900">{selectedItem.matchScore}%</p></div>
                <div><p className="text-zinc-500">Status</p><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(selectedItem.status)}>{selectedItem.status}</span></div>
              </div>
              <div>
                <p className="text-zinc-500 text-sm mb-1">Details</p>
                <div className="p-3 bg-zinc-50 rounded-none text-sm text-zinc-700 border border-zinc-100">{selectedItem.details}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Add Comment</label>
                <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add an analyst comment..."
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500" rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                {selectedItem.status !== 'cleared' && selectedItem.status !== 'escalated' && (
                  <button onClick={() => { handleEscalateAlert(selectedItem.id); setShowDetailModal(false); }}
                    className="flex items-center gap-2 px-4 py-2 border border-zinc-300 text-zinc-600 rounded-none hover:bg-zinc-50 text-sm font-medium">
                    <AlertTriangle className="w-4 h-4" />Escalate
                  </button>
                )}
                <button disabled={!commentText.trim()} onClick={() => { if (commentText.trim()) addAudit('Screening Comment', `Comment on ${selectedItem.id}: "${commentText}"`, selectedItem.id); closeModal(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Send className="w-4 h-4" />Submit Comment
                </button>
              </div>
            </>)}

            {/* Document Detail */}
            {selectedItem.uploadedBy && !selectedItem.makerId && selectedItem.matchScore === undefined && (<>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-zinc-500">Document Name</p><p className="font-medium text-zinc-900">{selectedItem.name}</p></div>
                <div><p className="text-zinc-500">Type</p><span className="px-2 py-1 rounded-none text-xs font-medium bg-zinc-100 text-zinc-700">{selectedItem.type}</span></div>
                <div><p className="text-zinc-500">Uploaded By</p><p className="font-medium text-zinc-900">{selectedItem.uploadedBy}</p></div>
                <div><p className="text-zinc-500">Date</p><p className="font-medium text-zinc-900">{selectedItem.date}</p></div>
                <div><p className="text-zinc-500">Status</p><span className="px-2 py-1 rounded-none text-xs font-medium" style={getStatusStyle(selectedItem.status)}>{selectedItem.status}</span></div>
                <div><p className="text-zinc-500">Case Reference</p><p className="font-mono text-sm text-zinc-600">{selectedItem.caseRef}</p></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">Add Comment</label>
                <textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment about this document..."
                  className="w-full px-3 py-2 border border-zinc-200 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500" rows={3} />
              </div>
              <div className="flex justify-end pt-2">
                <button disabled={!commentText.trim()} onClick={() => { if (commentText.trim()) addAudit('Document Commented', `Comment on ${selectedItem.id}: "${commentText}"`, selectedItem.id); closeModal(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-600 text-white rounded-none hover:bg-zinc-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Send className="w-4 h-4" />Submit Comment
                </button>
              </div>
            </>)}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ComplianceCheckerDashboard;
